import {
  type ExamType,
  type ExamWorksheetData,
  MSK_INDICATIONS,
  type MskIndication,
  type ProcedureExamType,
  type UsExamType,
} from "./epocus-exam-worksheets";

type DiagnosticExamType = Exclude<ExamType, ProcedureExamType | UsExamType>;

type IndicationValue<I> = I extends readonly (infer E)[] ? E : I;

type IndicationOf<T extends ExamType> = Extract<
  IndicationValue<Extract<ExamWorksheetData, { examType: T }>["indication"]>,
  string
>;

export type ExamTypeCategory =
  | "emergency"
  | "abdominal_renal"
  | "vascular"
  | "msk_soft_tissue"
  | "obstetric"
  | "procedures";

export const EXAM_TYPE_CATEGORY_LABELS: Record<ExamTypeCategory, string> = {
  emergency: "Emergency",
  abdominal_renal: "Abdominal & renal",
  vascular: "Vascular",
  msk_soft_tissue: "MSK & soft tissue",
  obstetric: "Obstetric",
  procedures: "Procedures",
};

export const US_EXAM_TYPE_CATEGORY: Record<
  UsExamType,
  Exclude<ExamTypeCategory, "procedures">
> = {
  us_efast: "emergency",
  us_cardiac: "emergency",
  us_thoracic: "emergency",
  us_cardiac_thoracic: "emergency",
  us_rush: "emergency",
  us_ocular: "emergency",
  us_biliary: "abdominal_renal",
  us_renal: "abdominal_renal",
  us_biliary_renal: "abdominal_renal",
  us_bladder: "abdominal_renal",
  us_appendix: "abdominal_renal",
  us_sbo: "abdominal_renal",
  us_testicular: "abdominal_renal",
  us_aorta: "vascular",
  us_dvt: "vascular",
  us_soft_tissue: "msk_soft_tissue",
  us_ob_transabdominal: "obstetric",
  us_ob_transvaginal: "obstetric",
};

export const EXAM_TYPE_CATEGORY: Record<ExamType, ExamTypeCategory> = {
  efast: "emergency",
  cardiac: "emergency",
  lung: "emergency",
  bladder: "abdominal_renal",
  renal: "abdominal_renal",
  aorta: "vascular",
  pregnancy: "obstetric",
  msk: "msk_soft_tissue",
  guided_joint_injection: "procedures",
  guided_tendon_injection: "procedures",
  guided_nerve_block: "procedures",
  ...US_EXAM_TYPE_CATEGORY,
};

export const US_EXAM_TYPE_LABELS: Record<UsExamType, string> = {
  us_efast: "E-FAST",
  us_cardiac: "Cardiac",
  us_thoracic: "Thoracic (lung)",
  us_renal: "Renal",
  us_bladder: "Bladder",
  us_aorta: "Abdominal aorta",
  us_ob_transabdominal: "Obstetric, transabdominal",
  us_cardiac_thoracic: "Cardiac + Thoracic",
  us_rush: "RUSH",
  us_biliary: "Biliary",
  us_biliary_renal: "Biliary + Renal",
  us_appendix: "Appendix",
  us_sbo: "Small bowel obstruction",
  us_ob_transvaginal: "Obstetric, transvaginal",
  us_dvt: "DVT",
  us_soft_tissue: "Soft tissue",
  us_ocular: "Ocular",
  us_testicular: "Testicular",
};

export const EXAM_TYPE_LABELS: Record<ExamType, string> = {
  efast: "EFAST",
  cardiac: "Cardiac",
  lung: "Lung",
  bladder: "Bladder",
  renal: "Renal",
  aorta: "Aorta",
  pregnancy: "Pregnancy",
  msk: "MSK",
  guided_joint_injection: "Guided Joint / Bursa Injection",
  guided_tendon_injection: "Guided Tendon Sheath Injection",
  guided_nerve_block: "Guided Nerve Block",
  ...US_EXAM_TYPE_LABELS,
};

// Indications are scoped per exam type, not global — the same value can appear
// under several exam types (hypotension under efast, cardiac and lung), so a
// flat value->label map would be ambiguous the moment two exam types disagreed
// on wording.
const INDICATION_LABELS: {
  [T in DiagnosticExamType]: Record<IndicationOf<T>, string>;
} = {
  efast: {
    blunt_trauma: "Blunt Trauma",
    penetrating_trauma: "Penetrating Trauma",
    hypotension: "Hypotension",
  },
  cardiac: {
    dyspnea: "Dyspnea",
    hypotension: "Hypotension",
    chest_pain: "Chest Pain",
    arrest: "Cardiac Arrest",
  },
  lung: {
    dyspnea: "Dyspnea",
    hypotension: "Hypotension",
    trauma: "Trauma",
  },
  bladder: {
    urinary_retention: "Urinary Retention",
    abdominal_pain: "Abdominal Pain",
    post_void_residual: "Post-void Residual",
  },
  renal: {
    flank_pain: "Flank Pain",
    hematuria: "Hematuria",
    aki: "AKI",
  },
  aorta: {
    abdominal_pain: "Abdominal Pain",
    pulsatile_mass: "Pulsatile Mass",
    syncope: "Syncope",
    aaa_screening: "AAA Screening",
  },
  pregnancy: {
    dating: "Dating",
    vaginal_bleeding: "Vaginal Bleeding",
    pelvic_pain: "Pelvic Pain",
    suspected_ectopic: "Suspected Ectopic",
    hyperemesis: "Hyperemesis",
  },
  msk: Object.fromEntries(
    Object.entries(MSK_INDICATIONS).map(([indication, { label }]) => [
      indication,
      label,
    ]),
  ) as Record<MskIndication, string>,
};

export const indicationOptions = <T extends ExamType>(
  examType: T,
): ReadonlyArray<{ value: IndicationOf<T>; label: string }> =>
  Object.entries(INDICATION_LABELS[examType as DiagnosticExamType] ?? {}).map(
    ([value, label]) => ({
      value: value as IndicationOf<T>,
      label: label as string,
    }),
  );

// Worksheets reach the patient history as untyped JSON off the sync API, so both
// lookups fall back to the raw value rather than rendering a blank.
export const examTypeLabel = (examType: string): string =>
  EXAM_TYPE_LABELS[examType as ExamType] ?? examType;

export const indicationLabel = (
  examType: string,
  indication: string,
): string => {
  const labels: Record<string, string> | undefined =
    INDICATION_LABELS[examType as DiagnosticExamType];

  return labels?.[indication] ?? indication;
};
